import { api, type SessionBoundRequestConfig } from "./client";

export interface AccountExport {
  archive: Blob;
  partial: boolean;
}

export async function requestAccountExport(password: string, signal: AbortSignal, expectedSession: string): Promise<AccountExport> {
  const response = await api.post<Blob>("/privacy/export", { password }, {
    responseType: "blob", signal, timeout: 75_000, expectedSession,
  } as SessionBoundRequestConfig);
  if (!(response.data instanceof Blob) || !response.data.size ||
      response.data.size > 64 * 1024 * 1024 ||
      !String(response.headers["content-type"] ?? "").startsWith("application/zip")) {
    throw new Error("invalid_export_response");
  }
  return { archive: response.data, partial: response.headers["x-export-status"] !== "complete" };
}

const knownErrors = new Set([
  "invalid_password", "demo_read_only", "invalid_request", "export_too_large",
  "rate_limited", "export_busy", "export_failed", "unsupported_schema", "export_changed",
]);

/** Blob errors are parsed only for known codes; never show server payload text. */
export async function exportErrorCode(error: unknown): Promise<string> {
  const response = (error as { response?: { data?: unknown; status?: number } })?.response;
  let body = response?.data;
  if (body instanceof Blob) {
    if (body.size > 8_192) return "export_failed";
    try { body = JSON.parse(await body.text()); } catch { return "export_failed"; }
  }
  const code = (body as { detail?: { code?: unknown } } | undefined)?.detail?.code;
  if (typeof code === "string" && knownErrors.has(code)) return code;
  if (response?.status === 429) return "rate_limited";
  if (response?.status === 413) return "export_too_large";
  return "export_failed";
}
